import { FC, createContext, useContext, useMemo, useState } from "react";

import {
	getSurfaceBorderColor,
	getSurfaceBorderStyle,
} from "@/app/application/theme";
import {
	GetLanguageValueAsString,
	Translate,
} from "@/app/application/Translate";
import { UnifiedPageHeader } from "@/app/application/UnifiedPageHeader";
import { TranslationContext, UserContext } from "@/app/providers";
import { checkAdminAccess } from "@/service/ApplicationQueries";
import {
	sharedFilterPanelSx,
	sharedFilterResetButtonSx,
	sharedSecondaryButtonSx,
	unifiedHeaderTokens,
} from "@/styles/sharedNavStyles";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	IconButton,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Select,
	TextField,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { Language } from "@/model/Language/Language";

export type SetTaskHeaderActions = (node: React.ReactNode) => void;
export type TaskHeaderPrimaryAction = {
	label: string;
	onClick: () => void;
};
export type SetTaskHeaderPrimaryAction = (
	action: TaskHeaderPrimaryAction | null,
) => void;

const TaskHeaderActionsContext = createContext<SetTaskHeaderActions | null>(
	null,
);
const TaskHeaderPrimaryActionContext =
	createContext<SetTaskHeaderPrimaryAction | null>(null);

export function useTaskHeaderActions() {
	const set = useContext(TaskHeaderActionsContext);
	return set ?? (() => {});
}

export function useTaskHeaderPrimaryAction() {
	const set = useContext(TaskHeaderPrimaryActionContext);
	return set ?? (() => {});
}

export const TaskHeaderActionsContextProvider =
	TaskHeaderActionsContext.Provider;
export const TaskHeaderPrimaryActionContextProvider =
	TaskHeaderPrimaryActionContext.Provider;

interface TaskPageHeaderProps {
	searchInput: string;
	onSearchChange: (value: string) => void;
	selectedGroupIds: number[];
	onGroupChange: (groupIds: number[]) => void;
	taskGroups: Array<{ Id: number; Name?: string }>;
	loading?: boolean;
	onOpenGlobalSearch: () => void;
	onOverviewClick: () => void;
	activeTab: "active" | "completed" | "recurring";
	completedDateFrom: string;
	onCompletedDateFromChange: (value: string) => void;
	completedDateTo: string;
	onCompletedDateToChange: (value: string) => void;
	completedOnlyHandover: boolean;
	onCompletedOnlyHandoverChange: (value: boolean) => void;
	completedOnlyShiftlog: boolean;
	onCompletedOnlyShiftlogChange: (value: boolean) => void;
	extraActions?: React.ReactNode;
	primaryAction?: TaskHeaderPrimaryAction | null;
	tabs: React.ReactNode;
}

export const TaskPageHeader: FC<TaskPageHeaderProps> = ({
	searchInput,
	onSearchChange,
	selectedGroupIds,
	onGroupChange,
	taskGroups,
	loading,
	onOpenGlobalSearch,
	onOverviewClick,
	activeTab,
	completedDateFrom,
	onCompletedDateFromChange,
	completedDateTo,
	onCompletedDateToChange,
	completedOnlyHandover,
	onCompletedOnlyHandoverChange,
	completedOnlyShiftlog,
	onCompletedOnlyShiftlogChange,
	extraActions,
	primaryAction,
	tabs,
}) => {
	const theme = useTheme();
	const user = useContext(UserContext);
	const isAdmin = Boolean(user && checkAdminAccess(user));
	const translation: Language | null = useContext(TranslationContext);
	const isMobile = useMediaQuery("(max-width:767px)");
	const controlHeight = isMobile ? 44 : unifiedHeaderTokens.buttonHeight;
	const [showFilters, setShowFilters] = useState(false);
	const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null);
	const controlSx = {
		height: controlHeight,
		minHeight: controlHeight,
		borderRadius: 2,
	};
	const selectSx = {
		"& .MuiInputBase-root": {
			...controlSx,
			bgcolor: "background.paper",
			border: getSurfaceBorderStyle(theme, "strong"),
			boxShadow: "none",
			"&.Mui-focused": {
				boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}`,
			},
		},
		"& .MuiSelect-select": {
			display: "flex",
			alignItems: "center",
			height: controlHeight,
			py: 0,
		},
	};
	const textFieldSx = {
		"& .MuiInputBase-root": {
			...controlSx,
			bgcolor: "background.paper",
			border: getSurfaceBorderStyle(theme, "strong"),
			boxShadow: "none",
			"&.Mui-focused": {
				boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}`,
			},
		},
		"& .MuiInputBase-input": {
			height: controlHeight,
			py: 0,
			display: "flex",
			alignItems: "center",
			fontSize: unifiedHeaderTokens.searchInputFontSize,
		},
	};

	const isCompletedTab = activeTab === "completed";
	const hasCompletedFilters = Boolean(
		completedDateFrom ||
		completedDateTo ||
		completedOnlyHandover ||
		completedOnlyShiftlog,
	);

	const selectedGroupLabel = useMemo(() => {
		const allowedGroupIds = new Set(taskGroups.map((group) => group.Id));
		const valid = selectedGroupIds.filter((groupId) =>
			allowedGroupIds.has(groupId),
		);
		const uniqueValid = [...new Set(valid)];
		if (uniqueValid.length === 0) return "Select groups";
		if (uniqueValid.length === 1) {
			return (
				taskGroups.find((group) => group.Id === uniqueValid[0])?.Name ||
				"1 group"
			);
		}

		return `${uniqueValid.length} groups selected`;
	}, [selectedGroupIds, taskGroups]);
	const effectiveSelectedGroupIds = useMemo(() => {
		const allowedGroupIds = new Set(taskGroups.map((group) => group.Id));
		return [...new Set(selectedGroupIds)].filter((groupId) =>
			allowedGroupIds.has(groupId),
		);
	}, [selectedGroupIds, taskGroups]);
	const hasActionsMenu = Boolean(extraActions);
	const actionsOpen = hasActionsMenu && Boolean(actionsAnchor);

	if (loading) {
		return null;
	}

	const globalSearchButton = (
		<Button
			onClick={onOpenGlobalSearch}
			variant="outlined"
			startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
			sx={{
				...sharedSecondaryButtonSx,
				height: controlHeight,
				minHeight: controlHeight,
				minWidth: { xs: "100%", md: 280 },
				justifyContent: "space-between",
				alignItems: "center",
				textTransform: "none",
				fontWeight: 900,
				borderRadius: 2,
				borderColor: getSurfaceBorderColor(theme, "strong"),
				bgcolor: theme.palette.background.paper,
				"&:hover": {
					borderColor: getSurfaceBorderColor(theme, "strong"),
					bgcolor: alpha(
						theme.palette.primary.main,
						theme.palette.mode === "dark" ? 0.08 : 0.035,
					),
				},
			}}
		>
			<Box sx={{ flex: 1, textAlign: "left", opacity: 0.85 }}>
				<Translate String="Auto.Features.Task.Ui.Components.TaskPageHeader.L210C60.SearchEverythingInTasks">
					Search everything in tasks...
				</Translate>
			</Box>
			<Box
				sx={{
					ml: 1,
					px: 0.75,
					py: 0.25,
					borderRadius: 1,
					fontSize: 12,
					fontWeight: 900,
					color: "text.secondary",
					border: getSurfaceBorderStyle(theme, "strong"),
				}}
			>
				Ctrl K
			</Box>
		</Button>
	);

	const localSearchField = (
		<TextField
			size="small"
			placeholder={GetLanguageValueAsString(
				translation,
				"Auto.Features.Task.Ui.Components.TaskPageHeader.L235C16.SearchInThisView",
				"Search in this view...",
			)}
			value={searchInput}
			onChange={(event) => onSearchChange(event.target.value)}
			fullWidth
			InputProps={{
				startAdornment: (
					<InputAdornment position="start" sx={{ mr: 0 }}>
						<SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
					</InputAdornment>
				),
				endAdornment: searchInput ? (
					<InputAdornment position="end">
						<IconButton size="small" onClick={() => onSearchChange("")}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</InputAdornment>
				) : undefined,
			}}
			sx={textFieldSx}
		/>
	);

	const groupSelect = (
		<FormControl
			size="small"
			sx={{
				width: { xs: "100%", sm: 220 },
				minWidth: 0,
				...selectSx,
			}}
		>
			<Select
				multiple
				displayEmpty
				value={effectiveSelectedGroupIds}
				onChange={(event: SelectChangeEvent<number[]>) => {
					const rawValue = event.target.value;
					const nextGroupIds = (Array.isArray(rawValue) ? rawValue : [rawValue])
						.map((value) => Number(value))
						.filter(
							(value): value is number => Number.isInteger(value) && value > 0,
						);
					onGroupChange([...new Set(nextGroupIds)]);
				}}
				renderValue={() => selectedGroupLabel}
				sx={{
					"& .MuiOutlinedInput-notchedOutline": {
						borderRadius: 2,
					},
				}}
			>
				{taskGroups.map((group) => (
					<MenuItem key={group.Id} value={group.Id}>
						<Checkbox
							checked={effectiveSelectedGroupIds.includes(group.Id)}
							size="small"
						/>
						<ListItemText primary={group.Name || `Group ${group.Id}`} />
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);

	const actionsMenu = extraActions ? (
		<Menu
			anchorEl={actionsAnchor}
			open={actionsOpen}
			onClick={() => setActionsAnchor(null)}
			onClose={() => setActionsAnchor(null)}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			transformOrigin={{ vertical: "top", horizontal: "right" }}
			MenuListProps={{ sx: { py: 0.5 } }}
			PaperProps={{
				sx: {
					borderRadius: 3,
					mt: 1,
					minWidth: 240,
					overflow: "hidden",
					border: (menuTheme) => `1px solid ${menuTheme.palette.divider}`,
					boxShadow:
						"0px 14px 30px rgba(0,0,0,0.12), 0px 6px 12px rgba(0,0,0,0.08)",
				},
			}}
		>
			{extraActions}
		</Menu>
	) : null;

	const secondaryActions = (
		<>
			{isCompletedTab && (
				<Button
					variant="outlined"
					size="small"
					startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
					onClick={() => setShowFilters((value) => !value)}
					color={showFilters ? "primary" : "inherit"}
					sx={{
						...sharedSecondaryButtonSx,
						pl: 1,
						pr: 0.5,
						bgcolor: showFilters ? "action.selected" : "transparent",
					}}
				>
					Filters
					<ExpandMoreIcon
						sx={{
							fontSize: 18,
							ml: 0.25,
							transition: "transform 150ms ease",
							transform: showFilters ? "rotate(180deg)" : "rotate(0deg)",
						}}
					/>
				</Button>
			)}
			{isAdmin && (
				<Button
					variant="outlined"
					size="small"
					startIcon={<InsightsOutlinedIcon sx={{ fontSize: 18 }} />}
					onClick={onOverviewClick}
					sx={sharedSecondaryButtonSx}
				>
					Overview
				</Button>
			)}
			{primaryAction && (
				<Button
					variant="contained"
					size="small"
					startIcon={<AddIcon sx={{ fontSize: 18 }} />}
					onClick={primaryAction.onClick}
					sx={sharedSecondaryButtonSx}
				>
					{primaryAction.label}
				</Button>
			)}
			{hasActionsMenu && (
				<Button
					variant="contained"
					size="small"
					onClick={(event) => setActionsAnchor(event.currentTarget)}
					endIcon={<span style={{ fontSize: 10 }}>▾</span>}
					sx={sharedSecondaryButtonSx}
				>
					Actions
				</Button>
			)}
		</>
	);

	const mobileActionButtonSx = {
		...sharedSecondaryButtonSx,
		...controlSx,
		flex: "1 1 160px",
		minWidth: 0,
		justifyContent: "center",
	};

	const searchOrFilter = isMobile ? (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				width: "100%",
			}}
		>
			{localSearchField}
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "stretch",
					gap: 0.75,
					width: "100%",
				}}
			>
				<Button
					variant="outlined"
					startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
					onClick={onOpenGlobalSearch}
					sx={mobileActionButtonSx}
				>
					<Translate String="Auto.Features.Task.Ui.Components.TaskPageHeader.L430C6.SearchAll">
						Search all
					</Translate>
				</Button>
				{isCompletedTab && (
					<Button
						variant="outlined"
						startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
						onClick={() => setShowFilters((value) => !value)}
						color={showFilters ? "primary" : "inherit"}
						sx={{
							...mobileActionButtonSx,
							bgcolor: showFilters ? "action.selected" : "transparent",
						}}
					>
						Filters
					</Button>
				)}
				{isAdmin && (
					<Button
						variant="outlined"
						startIcon={<InsightsOutlinedIcon sx={{ fontSize: 18 }} />}
						onClick={onOverviewClick}
						sx={mobileActionButtonSx}
					>
						Overview
					</Button>
				)}
				{primaryAction && (
					<Button
						variant="contained"
						startIcon={<AddIcon sx={{ fontSize: 18 }} />}
						onClick={primaryAction.onClick}
						sx={mobileActionButtonSx}
					>
						{primaryAction.label}
					</Button>
				)}
				{hasActionsMenu && (
					<Button
						variant="contained"
						onClick={(event) => setActionsAnchor(event.currentTarget)}
						sx={{
							...mobileActionButtonSx,
							px: 1.5,
						}}
					>
						Actions
					</Button>
				)}
			</Box>
			{groupSelect}
		</Box>
	) : (
		<Box
			sx={{
				display: "flex",
				flexDirection: { xs: "column", md: "row" },
				gap: 1,
				alignItems: { xs: "stretch", md: "center" },
				width: "100%",
			}}
		>
			<Box sx={{ flex: "0 0 auto" }}>{globalSearchButton}</Box>
			<Box sx={{ flex: 1, minWidth: 220 }}>{localSearchField}</Box>
			{groupSelect}
		</Box>
	);

	const filterBar =
		isCompletedTab && showFilters ? (
			<Box
				sx={[
					sharedFilterPanelSx(theme),
					{
						mt: 0.5,
						p: 1,
						borderRadius: 2,
					},
				]}
			>
				{hasCompletedFilters && (
					<Button
						variant="text"
						size="small"
						onClick={() => {
							onCompletedDateFromChange("");
							onCompletedDateToChange("");
							onCompletedOnlyHandoverChange(false);
							onCompletedOnlyShiftlogChange(false);
						}}
						sx={sharedFilterResetButtonSx}
					>
						Clear all
					</Button>
				)}
				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
						alignItems: { xs: "stretch", md: "center" },
						gap: 1,
						width: "100%",
					}}
				>
					<TextField
						type="date"
						size="small"
						hiddenLabel
						value={completedDateFrom}
						onChange={(event) => onCompletedDateFromChange(event.target.value)}
						InputLabelProps={{ shrink: true }}
						sx={{ width: { xs: "100%", sm: 160 } }}
					/>
					<TextField
						type="date"
						size="small"
						hiddenLabel
						value={completedDateTo}
						onChange={(event) => onCompletedDateToChange(event.target.value)}
						InputLabelProps={{ shrink: true }}
						sx={{ width: { xs: "100%", sm: 160 } }}
					/>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							gap: 0.5,
						}}
					>
						<FormControlLabel
							sx={{
								m: 0,
								px: 0.75,
								py: 0.25,
								borderRadius: 2,
								"&:hover": { bgcolor: "action.hover" },
							}}
							control={
								<Checkbox
									checked={completedOnlyHandover}
									onChange={(event) =>
										onCompletedOnlyHandoverChange(event.target.checked)
									}
									sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
								/>
							}
							label="Handover only"
						/>
						<FormControlLabel
							sx={{
								m: 0,
								px: 0.75,
								py: 0.25,
								borderRadius: 2,
								"&:hover": { bgcolor: "action.hover" },
							}}
							control={
								<Checkbox
									checked={completedOnlyShiftlog}
									onChange={(event) =>
										onCompletedOnlyShiftlogChange(event.target.checked)
									}
									sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
								/>
							}
							label="Shiftlog only"
						/>
					</Box>
				</Box>
			</Box>
		) : null;

	return (
		<UnifiedPageHeader
			searchOrFilter={
				<>
					{searchOrFilter}
					{actionsMenu}
				</>
			}
			secondaryActions={isMobile ? undefined : secondaryActions}
			row4Content={
				isCompletedTab ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							width: "100%",
							minHeight: 0,
						}}
					>
						{tabs}
						{filterBar}
					</Box>
				) : (
					tabs
				)
			}
		/>
	);
};

export function buildTaskHeaderMenuItem(label: string, onClick: () => void) {
	return (
		<MenuItem
			sx={{
				mx: 0.75,
				my: 0.25,
				borderRadius: 2,
				px: 1,
				py: 0.75,
				minHeight: 38,
				"& .MuiListItemIcon-root": {
					minWidth: 30,
					color: "text.primary",
				},
				"& .MuiListItemText-primary": {
					fontSize: 14.5,
					fontWeight: 500,
					letterSpacing: 0.1,
				},
				"&:hover": { backgroundColor: "action.hover" },
			}}
			onClick={onClick}
		>
			<ListItemIcon>
				<AddIcon fontSize="small" sx={{ color: "text.primary" }} />
			</ListItemIcon>
			<ListItemText primary={label} />
		</MenuItem>
	);
}
